import {
  Alert,
  AlertDescription,
  AlertTitle,
  Button,
  Card,
  Description,
  Heading,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Spinner,
} from "@peer-start/ui/reducate";
import { Search } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { useSession } from "../../prototype/api/queries.js";
import {
  useResetSituationCards,
  useSituationCardMutation,
  useSituationCards,
} from "../api/situation-card-queries.js";
import { SituationCardTable } from "../components/SituationCardTable.js";

function FilterSelect({
  controlId,
  label,
  onChange,
  options,
  value,
}: {
  controlId: string;
  label: string;
  onChange: (value: string) => void;
  options: readonly { value: string; label: string }[];
  value: string;
}) {
  return (
    <Select value={value || "all"} onValueChange={(next) => onChange(next === "all" ? "" : next)}>
      <SelectTrigger aria-label={label} className="w-full md:w-auto" data-control-id={controlId}>
        <SelectValue />
      </SelectTrigger>
      <SelectContent position="popper">
        {options.map((option) => (
          <SelectItem key={option.value || "all"} value={option.value || "all"}>
            {option.label}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

export function AdminSituationCardsPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const session = useSession();
  const currentUserId = session.data?.authenticated ? session.data.persona.id : undefined;
  const [q, setQ] = useState("");
  const [role, setRole] = useState<"" | "PO" | "VO" | "MBO">("");
  const [status, setStatus] = useState<"" | "published" | "concept" | "review" | "processing">("");
  const [page, setPage] = useState(1);
  const cards = useSituationCards({ page, q, role, status });
  const reset = useResetSituationCards();
  const beginEdit = useSituationCardMutation();

  if (cards.isLoading) {
    return (
      <div className="grid min-h-64 place-items-center">
        <span className="flex items-center gap-2 font-medium text-muted-foreground">
          <Spinner label={t("common.loading")} />
          <span aria-hidden="true">{t("common.loading")}</span>
        </span>
      </div>
    );
  }
  if (cards.isError || !cards.data) {
    return (
      <Alert variant="destructive">
        <AlertTitle>{t("common.error")}</AlertTitle>
        <AlertDescription>{t("administration.loadError")}</AlertDescription>
      </Alert>
    );
  }
  const data = cards.data;

  function editable(card: (typeof data.items)[number]) {
    return !(
      card.status === "processing" ||
      card.status === "review" ||
      (card.status === "concept" && currentUserId !== undefined && card.changedBy.id !== currentUserId)
    );
  }

  function openCard(card: (typeof data.items)[number]) {
    if (beginEdit.isPending) return;
    if (!editable(card)) {
      navigate(`/admin/situation-cards/${card.id}`);
      return;
    }
    beginEdit.mutate(
      { type: "begin-edit", cardId: card.id },
      { onSuccess: () => navigate(`/admin/situation-cards/${card.id}`) },
    );
  }
  const pages = Math.max(1, Math.ceil(data.total / data.pageSize));

  return (
    <>
      <header className="mb-8 flex flex-col justify-between gap-4 xl:flex-row xl:items-end">
        <div className="min-w-0">
          <Heading as="h1" level={1}>
            {t("administration.situationCardsTitle", { count: data.libraryCount })}
          </Heading>
          <Description className="mt-2 max-w-3xl">{t("administration.situationCardsSubtitle")}</Description>
        </div>
        <Button
          controlId="situation.list.reset"
          disabled={reset.isPending}
          onClick={() => {
            reset.mutate(undefined, {
              onSuccess: () => {
                setQ("");
                setRole("");
                setStatus("");
                setPage(1);
              },
            });
          }}
          type="button"
          variant="secondary"
        >
          {t("administration.situationCard.resetDemo")}
        </Button>
      </header>
      <div className="mb-5 flex flex-col gap-3 md:flex-row">
        <div className="relative min-w-0 flex-1">
          <Search
            aria-hidden="true"
            className="pointer-events-none absolute top-1/2 left-2.5 size-4 -translate-y-1/2 text-muted-foreground"
          />
          <Input
            aria-label={t("administration.situationCard.search")}
            className="pl-8"
            data-control-id="situation.list.search"
            onChange={(event) => {
              setQ(event.target.value);
              setPage(1);
            }}
            placeholder={t("administration.situationCard.search")}
            type="search"
            value={q}
          />
        </div>
        <FilterSelect
          controlId="situation.list.role"
          label={t("administration.situationCard.role")}
          onChange={(value) => {
            setRole(value as "" | "PO" | "VO" | "MBO");
            setPage(1);
          }}
          options={[
            { value: "", label: t("administration.situationCard.allRoles") },
            { value: "PO", label: "PO" },
            { value: "VO", label: "VO" },
            { value: "MBO", label: "MBO" },
          ]}
          value={role}
        />
        <FilterSelect
          controlId="situation.list.status"
          label={t("administration.situationCard.status")}
          onChange={(value) => {
            setStatus(value as "" | "published" | "concept" | "review" | "processing");
            setPage(1);
          }}
          options={[
            { value: "", label: t("administration.situationCard.allStatuses") },
            { value: "published", label: t("administration.situationCard.live") },
            { value: "concept", label: t("administration.situationCard.concept") },
            { value: "review", label: t("administration.situationCard.review") },
            { value: "processing", label: t("administration.situationCard.processing") },
          ]}
          value={status}
        />
      </div>
      {data.libraryCount === 0 ? (
        <Card>
          <Description>{t("administration.situationCardsEmpty")}</Description>
        </Card>
      ) : data.total === 0 ? (
        <Card>
          <Description>{t("administration.situationCard.noMatches")}</Description>
        </Card>
      ) : (
        <SituationCardTable
          canEdit={editable}
          cards={data.items}
          onOpen={openCard}
          onPage={setPage}
          page={page}
          pages={pages}
        />
      )}
    </>
  );
}
